import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, InfoIcon, LockIcon, PencilIcon, XIcon } from 'lucide-react';
import { Field } from '../types';
import { formatValue, initials } from '../utils/format';
interface InlineFieldProps {
  field: Field;
  value: string | boolean;
  dirty: boolean;
  compact?: boolean;
  onChange: (id: string, value: string | boolean) => void;
}
export function InlineField({
  field,
  value,
  dirty,
  compact = false,
  onChange
}: InlineFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string | boolean>(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>(null);
  useEffect(() => {
    if (editing) {
      setDraft(value);
      // focus once the input mounts
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [editing, value]);
  const commit = () => {
    onChange(field.id, draft);
    setEditing(false);
  };
  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };
  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && field.type !== 'textarea') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };
  const display = formatValue(field.type, value);
  const isEmpty = display === '';
  const canEdit = !field.readOnly;

  // ---- Checkbox renders inline (no separate edit mode) ----
  if (field.type === 'checkbox') {
    const checked = Boolean(value);
    return <FieldShell field={field} dirty={dirty} compact={compact} noEditButton data-id="element-125" data-mp="15p9kmf9bca9v">
        <button type="button" role="checkbox" aria-checked={checked} aria-label={field.label} onClick={() => onChange(field.id, !checked)} className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-md border transition-colors ${checked ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300 bg-white hover:border-blue-400'}`} data-id="element-126" data-mp="10wggntlo55yu">
          {checked && <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} data-id="element-127" data-mp="16oqkvjftqysz" />}
        </button>
      </FieldShell>;
  }
  return <FieldShell field={field} dirty={dirty} compact={compact} canEdit={canEdit} editing={editing} onEdit={() => canEdit && setEditing(true)} data-id="element-128" data-mp="1bms4q6l2r25i">
      <AnimatePresence mode="wait" initial={false} data-id="element-129" data-mp="iynz1l2i6p2t">
        {editing ? <motion.div key="editor" initial={{
        opacity: 0,
        y: -2
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.12
      }} className="flex items-start gap-2" data-id="element-130" data-mp="nysd74fxv7pv">
            <div className="flex-1" data-id="element-131" data-mp="1zfs53yydjkjd">
              {renderInput(field, draft, setDraft, inputRef, handleKey)}
            </div>
            <div className="flex shrink-0 items-center gap-1 pt-0.5" data-id="element-132" data-mp="3oujmxfop37ce">
              <button type="button" onClick={commit} aria-label="Save" className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-600 text-white transition-colors hover:bg-blue-700" data-id="element-133" data-mp="3oww61w66vnr1">
                <CheckIcon className="h-4 w-4" data-id="element-134" data-mp="3a5dp9w5i9p6m" />
              </button>
              <button type="button" onClick={cancel} aria-label="Cancel" className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50" data-id="element-135" data-mp="364legygzjc88">
                <XIcon className="h-4 w-4" data-id="element-136" data-mp="2r7ub6e46xgdc" />
              </button>
            </div>
          </motion.div> : <motion.div key="value" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.12
      }} onClick={() => canEdit && setEditing(true)} className={canEdit ? 'cursor-text' : ''} data-id="element-137" data-mp="ue922u5zneqr">
            {renderDisplay(field, value, display, isEmpty)}
          </motion.div>}
      </AnimatePresence>
    </FieldShell>;
}

/* ---------- Field frame (label + hover edit affordance) ---------- */

interface FieldShellProps {
  field: Field;
  dirty: boolean;
  children: React.ReactNode;
  canEdit?: boolean;
  editing?: boolean;
  compact?: boolean;
  noEditButton?: boolean;
  onEdit?: () => void;
}
function FieldShell({
  field,
  dirty,
  children,
  canEdit = true,
  editing = false,
  compact = false,
  noEditButton = false,
  onEdit
}: FieldShellProps) {
  return <div className={`group relative rounded-lg transition-colors ${compact ? 'px-2.5 py-1.5' : 'px-3 py-2.5'} ${dirty ? 'bg-amber-50 ring-1 ring-amber-200' : 'hover:bg-slate-50'}`} data-id="element-138" data-mp="9r2rw5o7mqt1">
      {dirty && <span className="absolute left-0 top-2 h-[calc(100%-16px)] w-1 rounded-full bg-amber-400" data-id="element-139" data-mp="3d9phe0egsv7r" />}
      <div className={`flex items-center gap-1.5 ${compact ? 'mb-0.5' : 'mb-1'}`} data-id="element-140" data-mp="38avzzww24k2b">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-141" data-mp="38cz1bd2ntzmc">
          {field.label}
        </span>
        {field.hint && <span className="group/hint relative inline-flex" data-id="element-142" data-mp="3haj62vzqee1p">
            <InfoIcon className="h-3 w-3 text-slate-400" data-id="element-143" data-mp="2xdy34n3neh" />
            <span className="pointer-events-none absolute left-1/2 top-5 z-20 w-52 -translate-x-1/2 rounded-md bg-slate-800 px-2.5 py-1.5 text-[11px] font-normal normal-case leading-snug text-white opacity-0 shadow-lg transition-opacity group-hover/hint:opacity-100" data-id="element-144" data-mp="1cj0ceey2g3">
              {field.hint}
            </span>
          </span>}
        {field.readOnly && <LockIcon className="h-3 w-3 text-slate-400" data-id="element-145" data-mp="isvkgessh8g0" />}
        {dirty && <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-amber-600" data-id="element-146" data-mp="duu5o62ebvac">
            Edited
          </span>}
      </div>

      {children}

      {!noEditButton && canEdit && !editing && <button type="button" onClick={onEdit} aria-label={`Edit ${field.label}`} className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md text-slate-400 opacity-0 transition-all hover:bg-white hover:text-blue-600 hover:shadow-sm group-hover:opacity-100 focus:opacity-100" data-id="element-147" data-mp="3bp6xv3vrltkh">
          <PencilIcon className="h-3.5 w-3.5" data-id="element-148" data-mp="3vc4m9e3v4rsv" />
        </button>}
    </div>;
}

/* ---------- Display renderers ---------- */

function renderDisplay(field: Field, value: string | boolean, display: string, isEmpty: boolean) {
  if (isEmpty) {
    return <span className="text-sm italic text-slate-300" data-id="element-149" data-mp="8657t0as01fn">Empty</span>;
  }
  if (field.type === 'user') {
    const name = String(value);
    return <span className="inline-flex items-center gap-2" data-id="element-150" data-mp="29yte5iipv4zn">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-[10px] font-semibold text-blue-700" data-id="element-151" data-mp="29zbk56v6kxkr">
          {initials(name)}
        </span>
        <span className="text-sm font-medium text-blue-700 hover:underline" data-id="element-152" data-mp="1xniyql3mybt0">{name}</span>
      </span>;
  }
  if (field.type === 'select') {
    return <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-medium text-slate-700" data-id="element-153" data-mp="1nqebehy20aam">
        {display}
      </span>;
  }
  if (field.type === 'currency' || field.type === 'percent') {
    return <span className="text-sm font-semibold tabular-nums text-slate-900" data-id="element-154" data-mp="2ges6aotuiqhy">{display}</span>;
  }
  return <span className="text-sm text-slate-800" data-id="element-155" data-mp="2bf77qfil8k5r">{display}</span>;
}

/* ---------- Editors ---------- */

function renderInput(field: Field, draft: string | boolean, setDraft: (v: string | boolean) => void, ref: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>, onKeyDown: (e: React.KeyboardEvent) => void) {
  const base = 'w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 shadow-sm outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100';
  if (field.type === 'textarea') {
    return <textarea ref={el => ref.current = el} value={String(draft)} onChange={e => setDraft(e.target.value)} onKeyDown={onKeyDown} rows={3} className={`${base} resize-none`} data-id="element-156" data-mp="2m4lfhr6r873f" />;
  }
  if (field.type === 'select') {
    return <div className="relative" data-id="element-157" data-mp="3is9behztussw">
        <select ref={el => ref.current = el} value={String(draft)} onChange={e => setDraft(e.target.value)} onKeyDown={onKeyDown} className={`${base} appearance-none pr-8`} data-id="element-158" data-mp="3ivnczo0o2xza">
          <option value="" data-id="element-159" data-mp="1kw17vfptxnq">— Select —</option>
          {field.options?.map(opt => <option key={opt} value={opt} data-id="element-160" data-mp="6bw0h7cb4rte">
              {opt}
            </option>)}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-161" data-mp="fqfyvguszkve" />
      </div>;
  }
  if (field.type === 'date') {
    return <input ref={el => ref.current = el} type="date" value={String(draft)} onChange={e => setDraft(e.target.value)} onKeyDown={onKeyDown} className={base} data-id="element-162" data-mp="phajkgseiceq" />;
  }
  const inputMode = field.type === 'currency' || field.type === 'percent' || field.type === 'number' ? 'decimal' : 'text';
  return <div className="relative" data-id="element-163" data-mp="6q9lduz3ukw8">
      {field.type === 'currency' && <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400" data-id="element-164" data-mp="6rkqpz8780pg">
          $
        </span>}
      <input ref={el => ref.current = el} type="text" inputMode={inputMode} value={String(draft)} onChange={e => setDraft(e.target.value)} onKeyDown={onKeyDown} className={`${base} ${field.type === 'currency' ? 'pl-6' : ''} ${field.type === 'percent' ? 'pr-6' : ''}`} data-id="element-165" data-mp="1i4wwifniqte9" />
      {field.type === 'percent' && <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400" data-id="element-166" data-mp="16ci76o56595b">
          %
        </span>}
    </div>;
}